import { NgClass, NgTemplateOutlet } from "@angular/common";
import {
  Component,
  Input,
  OnInit,
  TemplateRef,
  ViewChild,
  inject,
  signal,
  computed,
} from "@angular/core";
import { IpCountryService } from "@app/services/ip-country.service";
import { TranslateModule } from "@ngx-translate/core";
import { InlineSVGModule } from "ng-inline-svg-w";
import { LanguageService } from "src/app/core/services/language.service";
import { Book } from "src/app/models/shared-models/book.model";
import {
  CreativeCommonsLicenseType,
  CreativeCommonsLicenseTypeIcon,
  CreativeCommonsLicenseTypeTranslation,
} from "src/app/models/shared-models/license-type-creative-commons.enum";
import { LicenseType } from "src/app/models/shared-models/license-type.enum";
import { PREORDER_WEEKS } from "src/app/models/shared-models/NEW_DAYS";
import {
  SupplyState,
  getSupplyState,
} from "src/app/models/shared-models/product-availability.enum";
import { ProductSupply } from "src/app/models/shared-models/product-supply.model";
import { ProductService } from "src/app/services/product.service";
import { Router } from "@angular/router";
import { PriceService } from "src/app/services/price.service";
import { FoxycartService } from "src/app/services/foxycart.service";
import { PopupComponent } from "../../../components/popup/popup.component";

type PurchaseButtonState =
  | "indiepubs"
  | "preorder-print"
  | "preorder-electronic"
  | "default";

@Component({
  selector: "app-buy-button",
  templateUrl: "./buy-button.component.html",
  styleUrls: ["./buy-button.component.scss"],
  standalone: true,
  imports: [
    NgClass,
    NgTemplateOutlet,
    InlineSVGModule,
    PopupComponent,
    TranslateModule,
  ],
})
export class BuyButtonComponent implements OnInit {
  languageService = inject(LanguageService);
  priceService = inject(PriceService);
  private foxycartService = inject(FoxycartService);
  private productService = inject(ProductService);
  private ipCountry = inject(IpCountryService);
  private router = inject(Router);

  @Input() productSupply!: ProductSupply;
  @Input() book!: Book;
  country = signal<string | undefined>(undefined);

  ccLicenseType = CreativeCommonsLicenseType;
  ccLicenseTypeTranslation = CreativeCommonsLicenseTypeTranslation;
  ccLicenseTypeIcon = CreativeCommonsLicenseTypeIcon;

  @ViewChild("printBook", { static: true })
  printBookTemplate!: TemplateRef<any>;
  @ViewChild("openAccessBook", { static: true })
  openAccessBookTemplate!: TemplateRef<any>;
  @ViewChild("notAvailable", { static: true })
  notAvailableTemplate!: TemplateRef<any>;

  /** True while an add-to-cart request is in flight (guards double-submit). */
  addingToCart = signal(false);
  /** Set when the last add-to-cart attempt failed, so the UI can react. */
  addToCartFailed = signal(false);

  isNorthAmericanUser = computed(() => {
    return ["US", "CA"].includes(this.country() ?? "");
  });

  purchaseButtonState = computed<PurchaseButtonState>(() => {
    if (this.isNorthAmericanUser() && !this.isElectronic()) return "indiepubs";
    if (this.isPreorder() && !this.isElectronic()) return "preorder-print";
    if (this.isPreorder() && this.isElectronic()) return "preorder-electronic";
    return "default";
  });

  onPurchaseButtonClick(): void {
    switch (this.purchaseButtonState()) {
      case "indiepubs":
        this.redirectToIndiePubs();
        break;
      case "preorder-print":
        this.redirectToDGBPreorder();
        break;
      case "preorder-electronic":
        this.electronicRedirectToDGBPreorder();
        break;
      default:
        this.addToCart();
        break;
    }
  }

  async ngOnInit(): Promise<void> {
    // Auto-resume an add-to-cart that was interrupted by the DGB session
    // redirect: only the buy-button whose ISBN matches the deferred add
    // completes it (now that we hold the shared session), then lands on the
    // cart. Claim it before any await so a slow GeoIP lookup can neither
    // delay the resume nor let a manual click double-add.
    const resume =
      this.foxycartService.peekPendingAdd() === this.productSupply?.isbn;
    if (resume) this.foxycartService.clearPendingAdd();

    const country = this.loadCountry();
    if (resume) await this.addToCart();
    await country;
  }

  private async loadCountry(): Promise<void> {
    try {
      const countryCode = await this.ipCountry.getCountry();
      this.country.set(countryCode);
    } catch (error) {
      console.error("Failed to get country code", error);
      this.country.set("");
    }
  }

  getTemplate(): TemplateRef<any> {
    if (this.isNotAvailable()) return this.notAvailableTemplate;

    // OA e-books cannot be pre-ordered — show "Available soon" instead of download button
    //
    // isNotAvailable() has already withheld the Upcoming state above, so the
    // pre-order check covers every forthcoming supply that reaches here.
    if (this.isOpenAccess()) {
      if (this.isPreorder()) return this.notAvailableTemplate;
      return this.openAccessBookTemplate;
    }

    return this.printBookTemplate;
  }

  /**
   * Purchase state the feed declares for this supply.
   *
   * De Gruyter Brill releases a title by dropping `delivery/planned_pub_date`,
   * not by changing its availability code: a forthcoming e-book carries code
   * 20 from its first delivery. `publishingDate` cannot carry the signal
   * either — it holds the planned date while forthcoming and the ONIX date
   * once published, and the publication delivery can land before that date.
   *
   * `SupplyState.Unknown` means the supply predates `plannedPublishingDate`
   * and has not been re-parsed, so every predicate below falls back to its
   * pre-change date-driven behaviour.
   *
   * The payload separates the two absent values. `plannedPublishingDate` is
   * declared without a default in
   * `aag-backend/src/models/mongoose-schemas/product-supply.schema.ts`, so a
   * supply written before the parser learned the field carries no key, and a
   * lean read hands it over with the key still missing: `undefined` is a
   * legacy supply. A serialised `null` is written on purpose, by the
   * publication delivery dropping `delivery/planned_pub_date`: it means
   * published. Reading either as the other flips a live title.
   */
  private supplyState(): SupplyState {
    return getSupplyState({
      availability: this.productSupply.availability,
      plannedPublishingDate: this.productSupply.plannedPublishingDate,
      weeksUntilPlannedPublishing: this.weeksUntilPlannedPublishing(),
    });
  }

  private weeksUntilPlannedPublishing(): number | undefined {
    try {
      // An unparseable date yields NaN rather than throwing, and NaN reads as
      // a known date everywhere downstream. Report it as no date.
      const weeks = this.productService.getPublishingDateWeeksDifference(
        this.productSupply.plannedPublishingDate ?? undefined,
      );
      return Number.isFinite(weeks) ? weeks : undefined;
    } catch {
      return undefined;
    }
  }

  isOutOfPrint(): boolean {
    return this.supplyState() === SupplyState.OutOfPrint;
  }

  isNotAvailable(): boolean {
    // Withhold a supply the reader cannot get: withdrawn, out of stock, or
    // planned beyond the pre-order window.
    //
    // Known copy compromise: out of stock (30-39) reuses the notAvailable
    // template, whose text is BOOK.info-4wk-12mo ("Available soon"), so a
    // backlist title awaiting a reprint reads as upcoming. Withholding it
    // matters more than the wording, and no Jovis supply carries a 30-39 code
    // today. An out-of-stock key is filed separately.
    const state = this.supplyState();
    if (state !== SupplyState.Unknown) {
      return (
        state === SupplyState.OutOfPrint ||
        state === SupplyState.OutOfStock ||
        state === SupplyState.Upcoming
      );
    }

    // No out-of-print check here: an availability code in 40-49 resolves to
    // OutOfPrint whatever the planned date says, so this branch runs only for
    // a supply isOutOfPrint() already reports as false.

    // if the publishing date is more than 6 weeks in the future
    try {
      const diffInWeeks = this.productService.getPublishingDateWeeksDifference(
        this.productSupply.publishingDate,
      );
      return diffInWeeks > PREORDER_WEEKS;
    } catch {
      return true;
    }
  }

  isPreorder(): boolean {
    const state = this.supplyState();
    if (state !== SupplyState.Unknown) return state === SupplyState.Preorder;

    // if the publishing date is in the future but not more than 6 weeks
    try {
      const diffInWeeks = this.productService.getPublishingDateWeeksDifference(
        this.productSupply.publishingDate,
      );
      return diffInWeeks > 0 && diffInWeeks <= PREORDER_WEEKS;
    } catch {
      return false;
    }
  }

  async addToCart() {
    if (this.addingToCart()) return;
    this.addingToCart.set(true);
    this.addToCartFailed.set(false);
    let navigatingAway = false;
    try {
      // Land the item in a session worth adding to: when the feature is on,
      // this redirects to DGB (returns false) if no session is held or the
      // held one is empty and hasn't been through DGB yet — remember this add
      // so it auto-resumes on return, and stay busy: the page is unloading
      // and a second click must not add into a session we are about to
      // leave. Inert (true) when the flag is unset.
      if (!(await this.foxycartService.ensureSharedSession())) {
        this.foxycartService.rememberPendingAdd(this.productSupply.isbn);
        navigatingAway = true;
        return;
      }

      const currency = this.priceService.currency;
      const lang = this.productSupply.language;

      const { fields, extraParams } = await this.foxycartService.getCartLink(
        this.productSupply.isbn,
        currency,
        lang,
      );

      await this.foxycartService.addItem(fields, extraParams);
      // Surface the (local) cart after adding, like the previous on-site cart.
      await this.router.navigate(["/", lang, "cart"]);
    } catch (error) {
      console.error("Failed to add item to cart", error);
      this.addToCartFailed.set(true);
    } finally {
      if (!navigatingAway) this.addingToCart.set(false);
    }
  }

  isElectronic() {
    return this.productSupply?.format.toLowerCase() == "electronic";
  }

  downloadPDF() {
    const doi = this.book.bookData?.doi || `10.1515/${this.productSupply.isbn}`;
    const downloadOpenAccessUrl = `https://www.degruyterbrill.com/document/doi/${doi}/pdf`;
    window.open(downloadOpenAccessUrl, "_blank", "noopener");
  }

  isOpenAccess(): boolean {
    return this.productSupply?.license == LicenseType.OpenAccess;
  }

  redirectToIndiePubs(): void {
    const indiePubsUrl = `https://indiepubs-us-refresh.myshopify.com/cart?isbn=${this.productSupply.isbn}&publishername=de-gruyter`;
    window.open(indiePubsUrl, "_blank", "noopener");
  }

  redirectToDGBPreorder(): void {
    let dgbPreorderUrl: string;
    if (this.productSupply.format.toLowerCase() === "hardback") {
      dgbPreorderUrl = `https://www.degruyterbrill.com/document/isbn/${this.productSupply.isbn}/preorder?medium=hardcover`;
      window.open(dgbPreorderUrl, "_blank", "noopener");
    } else if (this.productSupply.format.toLowerCase() === "paperback") {
      dgbPreorderUrl = `https://www.degruyterbrill.com/document/isbn/${this.productSupply.isbn}/preorder?medium=paperback`;
      window.open(dgbPreorderUrl, "_blank", "noopener");
    }
  }

  electronicRedirectToDGBPreorder(): void {
    const dgbPreorderUrl = `https://www.degruyterbrill.com/document/isbn/${this.productSupply.isbn}/preorder?medium=PDF`;
    window.open(dgbPreorderUrl, "_blank", "noopener");
  }
}
